import {Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild} from '@angular/core';
import { NgForm } from "@angular/forms";

import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { MessageService } from '@app/core/message.service';
import { PaymentsService } from '@app/services/net-remit/payments.service';

import { ErrorView } from '@app/shared/models/ErrorView';
import { ComboKeysView } from '@app/shared/models/ComboKeysView';
import { AgentsMacroGroupView } from '@app/shared/views/AgentsMacroGroupView';
import { MemoPaymentView } from '@app/shared/views/MemoPaymentView';
import { FileTypeView } from '@app/shared/views/FileTypeView';
import { RemoteAccountView } from '@app/shared/views/RemoteAccountView';
import { AirlineCountriesView } from '@app/shared/models/AirlineCountriesView';
import { CountryView } from '@app/shared/models/ipm/view/CountryView';
import { CustomerEntityView } from '@app/shared/models/CustomerEntityView';

import { PaymentPeriodicFormComponent } from "@app/payment-management/payment-periodic-form/payment-periodic-form.component";

import { default as constantsFile } from '@app/shared/constants.json';

import { fadeIn } from 'assets/animations/animations';

@Component({
    selector: 'app-payment-form',
    templateUrl: './payment-form.component.html',
    styleUrls: ['./payment-form.component.scss'],
    animations: [fadeIn],
    standalone: false
})
export class PaymentFormComponent implements OnInit, OnDestroy {

  // Variables
  alive: boolean = true;
  constants: any = constantsFile.constants;
  errorFileSequenceRange: boolean;
  isAdmin: boolean;
  frequency: string;
  isAcmAdmEnabled: boolean;

  // Valores de constantes
  pattern: any = this.constants.pattern;
  fare: string = this.constants.paymentTypes.fare;
  bsp: string = this.constants.memoContentOrigin.bsp;
  manualCaptureBspConst: string = this.constants.memoContentOrigin.manualCaptureBsp;
  bspMemoContentOriginList: Array<string> = new Array<string>(this.manualCaptureBspConst, this.bsp);
  singleStr: string = this.constants.memoFrequency.single;
  periodicStr: string = this.constants.memoFrequency.periodic;
  periodRangeStr: string = this.constants.memoFrequency.periodRange;
  paymentsARCTypes = Object.values(this.constants.paymentsARCTypes);

  patternDoc = {
    numeric: '^\\d+$' // Only allows one or more digits (0-9)
  };

  // Variables de control
  memoContentByText:        boolean;
  memoContentByFile:        boolean;

  frequencyTouched:         boolean;
  memoContentOriginTouched: boolean;
  customerTouched:          boolean;
  airlineTouched:           boolean;
  countryTouched:           boolean;
  docTypeTouched:           boolean;
  fromDocTouched:           boolean;
  toDocTouched:             boolean;
  fromDocAcmTouched:        boolean;
  fromDocAdmTouched:        boolean;
  toDocAcmTouched:          boolean;
  toDocAdmTouched:          boolean;
  paymentTypeTouched:       boolean;
  vatTouched:               boolean;
  maxRelatedTouched:        boolean;
  fileSequenceTouched:      boolean;
  contactNameTouched:       boolean;
  phoneTouched:             boolean;
  memoContentTouched:       boolean;
  memoContentFileTouched:   boolean;
  emailTouched:             boolean;
  reasonForMemoTouched:     boolean;
  isArc:                    boolean;
  uploadRemoteTouched:      boolean;
  isDisabledAcm:            boolean;
  isDisabledAdm:            boolean;

  // Errores
  fileError: ErrorView = null;

  // Constants
  cancelled: string         = this.constants.memoStatus.cancelled;
  errorCreatingFile: string = this.constants.memoStatus.errorCreatingFile;
  pending: string           = this.constants.memoStatus.pending;
  pendingAudit: string      = this.constants.memoStatus.pendingAudit;
  prepared: string          = this.constants.memoStatus.prepared;
  sending: string           = this.constants.memoStatus.sending;
  inputTypeFile: string     = this.constants.paymentsFileTypes.input;
  outputTypeFile: string    = this.constants.paymentsFileTypes.output;
  excelTypeFile: string     = this.constants.paymentsFileTypes.excel;

  @ViewChild('paymentForm') paymentForm: NgForm;
  @ViewChild(PaymentPeriodicFormComponent) periodicFormComponent: PaymentPeriodicFormComponent;

  // Alerts
  @Output() cancelPaymentAlertEvent = new EventEmitter<void>();
  @Output() reprocessPaymentAlert = new EventEmitter<void>();
  @Output() updateStatusAlert = new EventEmitter<string>();
  @Output() showHideMemosListAlert = new EventEmitter<void>();
  @Output() viewTaskDetailAlert = new EventEmitter<MemoPaymentView>();

  // Remotos
  @Input() enableRemotes: boolean;
  @Input() uploadRemoteList: Array<RemoteAccountView>
  @Input() remoteResponseList: Array<RemoteAccountView>;

  //Datos comunes
  @Input() payment: MemoPaymentView;
  @Input() isNewPayment: boolean;
  @Input() paymentSavedOk: boolean;
  @Input() isTaskDetail: boolean;
  @Input() isTaskEditable: boolean;
  @Input() generalError: string;
  @Input() isLoading: boolean;
  @Input() isLoadingFileSequence: boolean;
  @Input() isLoadingCustomer: boolean;
  @Input() minFileSequence: number;
  @Input() maxFileSequence: number;
  @Input() paymentDefaultTranslated: string;
  @Input() endTaskDateNotHigherThanStartDate: boolean;
  @Input() endTaskDateNotHigherThanToday: boolean;

  // Creacion
  @Input() diffValueMaxRelated: number;
  @Input() isMaxRelatedHigherMax: boolean;
  @Input() isMaxRelatedZero: boolean;
  @Input() isSinglePayment: boolean;
  @Input() isPeriodicPayment: boolean;
  @Input() isPeriodRangePayment: boolean;
  @Input() defaultMaxRelated: number;
  @Input() diffCountry: string;
  @Input() airlineList: Array<AirlineCountriesView> = new Array<AirlineCountriesView>();
  @Input() countryList: Array<CountryView> = new Array<CountryView>();
  @Input() agentsMacroGroupsList: AgentsMacroGroupView[] = [];
  @Input() docTypesList: FileTypeView[];
  @Input() frequencyList: ComboKeysView[];
  @Input() fileInput: File;
  @Input() paymentTypesList: ComboKeysView[];
  @Input() originContent: Array<ComboKeysView>;
  @Input() isLoadingOriginContent: boolean;
  @Input() isLoadingMacroGroups: boolean;
  @Input() isLoadingRemotes: boolean;
  @Input() periodic: string;
  @Input() memoContentOriginSelected: string;
  @Input() customerList: Array<CustomerEntityView>;
  @Input() docTypeError       = new ErrorView("","","");
  @Input() NameError          = new ErrorView("","","");
  @Input() airlineError       = new ErrorView("","","");
  @Input() bspError           = new ErrorView("","","");
  @Input() maxRelatedError    = new ErrorView("","","");
  @Input() fileSequenceError  = new ErrorView("","","");
  @Input() paymentError       = new ErrorView("","","");
  @Input() paymentTypeError   = new ErrorView("","","");
  @Input() vatError           = new ErrorView("","","");
  @Input() fromDocumentError  = new ErrorView("","","");
  @Input() toDocumentError    = new ErrorView("","","");
  @Input() contactNameError   = new ErrorView("","","");
  @Input() contactPhoneError  = new ErrorView("","","");
  @Input() contactEmailError  = new ErrorView("","","");
  @Input() RMICError          = new ErrorView("","","");
  @Input() reasonForMemoError = new ErrorView("","","");
  @Input() memoContentError   = new ErrorView("","","");
  @Input() endTaskDateError   = new ErrorView("","","");

  //Detalle
  @Input() entryFileDisable: boolean;
  @Input() showMemosList: boolean; // Receive the value from the parent component
  @Input() cancelEnable: boolean; // Receive the value from the parent component
  @Input() reprocessableEnable: boolean; // Receive the value from the parent component
  @Input() sendBspLinkEnable: boolean; // Receive the value from the parent component
  @Input() rejectFileEnable: boolean; // Receive the value from the parent component

  @Output() frequencyChoiceMethod                   = new EventEmitter();
  @Output() memoContentOriginChoiceMethod           = new EventEmitter();
  @Output() modalChangeFrequencyMethod              = new EventEmitter();
  @Output() customerSelectionMethod                 = new EventEmitter();
  @Output() airlineSelectionMethod                  = new EventEmitter();
  @Output() paymentTypeSelectionMethod              = new EventEmitter<string>();
  @Output() countrySelectionMethod                  = new EventEmitter();
  @Output() maxRelatedSelectionMethod               = new EventEmitter();
  @Output() goBackMethod                            = new EventEmitter();
  @Output() updateMaxRelatedMethod                  = new EventEmitter();
  @Output() modalAffirmativeMethod                  = new EventEmitter();
  @Output() getFileToDownloadMethod                 = new EventEmitter<string>();
  @Output() clearErrorsMethod                       = new EventEmitter();
  @Output() attachmentFileMethod                    = new EventEmitter<Object>();
  @Output() getRemotesMethod                        = new EventEmitter();
  @Output() setEnableRemotesMethod                  = new EventEmitter<boolean>();
  @Output() endTaskDateNotHigherThanStartDateMethod = new EventEmitter();
  @Output() setEndTaskDateNotHigherThanTodayMethod  = new EventEmitter();

  constructor(
    private authService: AuthenticationService,
    private messageService: MessageService,
    private paymentService: PaymentsService
  ) {
    this.initErrorValues();
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.credentials.roleName.includes(this.constants.roleNames.admin);
    if(this.isNewPayment){
      this.paymentService.observableInitFields$.subscribe(() => {
        this.initErrorValues();
      });
    }
    // Hay que evaluar siempre si es Arc, porque aun siendo nuevo pago, al cargar, inicia este componente y por lo tanto pierde el valor.
    this.isArc = (this.payment?.bsp === this.diffCountry);
    if (this.isTaskDetail) {
      this.isArc = !this.bspMemoContentOriginList.some(mco => mco == this.payment.memoContentOrigin.messageKey);
    }
    // Evaluamos si el ACM+ADM está activo en la cfg.Configurations
    this.paymentService.isAcmAdmEnabled().subscribe(data => {
      this.isAcmAdmEnabled = data == "1";
    })

    // Seteamos la frecuencia del pago
    if(this.payment.sIsPeriodic == "Y") {
      this.frequency = this.periodicStr;
      this.isPeriodicPayment = true;
    } else if (this.payment.periodFrom != null) {
      this.frequency = this.periodRangeStr;
      this.isPeriodRangePayment = true;
    } else {
      this.frequency = this.singleStr;
      this.isSinglePayment = true;
    }
  }

  private initErrorValues(){
    this.clearErrorsMethod.emit();

    this.fileError = null;
    this.errorFileSequenceRange   = false;
    this.memoContentByText        = false;
    this.memoContentByFile        = false;

    this.frequencyTouched         = false;
    this.memoContentOriginTouched = false;
    this.customerTouched          = false;
    this.airlineTouched           = false;
    this.countryTouched           = false;
    this.docTypeTouched           = false;
    this.fromDocTouched           = false;
    this.toDocTouched             = false;
    this.fromDocAcmTouched        = false;
    this.fromDocAdmTouched        = false;
    this.toDocAcmTouched          = false;
    this.toDocAdmTouched          = false;
    this.paymentTypeTouched       = false;
    this.vatTouched               = false;
    this.maxRelatedTouched        = false;
    this.fileSequenceTouched      = false;
    this.contactNameTouched       = false;
    this.phoneTouched             = false;
    this.memoContentTouched       = false;
    this.memoContentFileTouched   = false;
    this.emailTouched             = false;
    this.reasonForMemoTouched     = false;
    this.uploadRemoteTouched      = false;

    this.messageService.clear();
  }

  private getRemotes() {
    this.getRemotesMethod.emit();
  }

  getStatusColor(status: number): string {
    switch (status) {
      case 10:
      case 20:
      case 21:
      case 25:
        return 'orange';
      case 30:
      case 50:
        return 'green';
      default:
        return 'red';
    }
  }

  // Selections
  public frequencyChoice(ev){
    if(ev.value != null){
      this.frequencyChoiceMethod.emit();
    }
  }

  public memoContentOriginChoice(){
    if(this.payment.memoContentOrigin) {
      this.isArc = !this.bspMemoContentOriginList.some(mco => mco == this.payment.memoContentOrigin.messageKey);
    }

    this.memoContentOriginChoiceMethod.emit();
  }

  public customerSelection() {
    this.customerSelectionMethod.emit();

    this.setRemotesAsUndefined();
  }

  public airlineCountrySelection(method: string) {
    this[method].emit();
    if (this.payment.customer?.customerId && this.payment.airline && this.payment.bsp) {
      this.getRemotes();
    } else {
      this.setRemotesAsUndefined();
    }
  }

  public onChangeDocType() {
    this.payment.docType.fileCode === "ACM" || this.payment.docType.fileCode === "ACM+ADM" ? this.isDisabledAcm = false : this.isDisabledAcm = true;
    this.payment.docType.fileCode === "ADM" || this.payment.docType.fileCode === "ACM+ADM" ? this.isDisabledAdm = false : this.isDisabledAdm = true;
  }

  public isAllowToSetRemotes(e : any) {
    if (!e.checked) {
      this.payment.remoteUpload = undefined;
      this.payment.remoteResponse= undefined;
    }
  }

  private setRemotesAsUndefined() {
    this.setEnableRemotesMethod.emit(false);
    this.uploadRemoteList = undefined;
    this.remoteResponseList = undefined;
    this.payment.remoteUpload = undefined;
    this.payment.remoteResponse= undefined;
    this.payment.isSFTPUploadable = false;
  }

  public maxRelatedSelection() {
    this.maxRelatedSelectionMethod.emit();
  }

  public paymentTypeSelection(){
    this.paymentTypeSelectionMethod.emit();
  }

  public fileSequenceSelection() {
    this.errorFileSequenceRange = (this.payment.fileSequence > this.maxFileSequence) || (this.payment.fileSequence < this.minFileSequence);
  }

  public getFileToDownload(typeFile: string){
    this.getFileToDownloadMethod.emit(typeFile);
  }

  public checkMemoPaymentFileSequence(): boolean{
    let cond = !this.payment.bsp || this.isLoadingFileSequence;
    if(cond && this.isNewPayment) this.payment.fileSequence = null;
    return cond;
  }

  public attachmentFile(info){
    this.attachmentFileMethod.emit(info);
  }

  public setEndTaskDateNotHigherThanStartDate(){
    this.endTaskDateNotHigherThanStartDateMethod.emit();
  }

  public setEndTaskDateNotHigherThanToday(){
    this.setEndTaskDateNotHigherThanTodayMethod.emit();
  }

  public isARCType(): boolean {
    return this.paymentsARCTypes.some(psa => psa == this.payment.memoContentOrigin?.messageKey);
  }

  onEnter(event) {
    event.preventDefault();
  }
  ngOnDestroy(): void {
		// prevent memory leak when component destroyed
    this.alive = false;
  }

  triggerCancelPaymentAlert() {
    this.cancelPaymentAlertEvent.emit();
  }

  triggerReprocessPaymentAlert() {
    this.reprocessPaymentAlert.emit();
  }

  triggerUpdateStatusAlert(status: string) {
    this.updateStatusAlert.emit(status);
  }

  triggerShowHideMemosListAlert() {
    this.showHideMemosListAlert.emit();
  }

  triggerViewTaskDetailAlert(payment: MemoPaymentView) {
    this.viewTaskDetailAlert.emit(payment);
  }

  get periodicForm() {
    return this.periodicFormComponent?.paymentPeriodicForm;
  }
}
